import React, { useState } from "react"
import styled from "styled-components"
import Loading from "src/app/admin/loading"
import { boxShadow } from "src/assets/styles"
import { color, font } from "@smoothincorg/smooth-ui"
import { Wrapper } from "src/components/page-directory/Wrapper"
import { Button } from "src/components/Button"
import { StoreInList } from "src/hooks/useStoresSWR"

type StoreSelection = {
  storeId: number
  pop: boolean
  flyer: boolean
}

type ContainerProps = {
  stores: StoreInList[]
  onLoadMore: () => void
  hasMore: boolean
  onCreatePdf: (selections: StoreSelection[]) => Promise<void>
  onRefresh: () => void
  isCreating?: boolean
  isRefreshing?: boolean
  selections?: StoreSelection[]
  onSelectionsChange?: (selections: StoreSelection[]) => void
}

type Props = ContainerProps & {
  className?: string
}

const Component: React.VFC<Props> = (props) => {
  const [internalSelections, setInternalSelections] = useState<StoreSelection[]>([])

  // 親から渡されたselectionsを使う場合はそれを優先、そうでなければinternalを使う
  const selections = props.selections || internalSelections
  const setSelections = props.onSelectionsChange || setInternalSelections

  if (!props.stores) return <Loading />

  const handleStoreSelect = (
    storeId: number,
    type: "pop" | "flyer",
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const checked = event.target.checked
    const existing = selections.find((s) => s.storeId === storeId)
    const newSelections = existing
      ? selections.map((s) => (s.storeId === storeId ? { ...s, [type]: checked } : s))
      : [
          ...selections,
          {
            storeId,
            pop: type === "pop" ? checked : false,
            flyer: type === "flyer" ? checked : false,
          },
        ]
    setSelections(newSelections)
  }

  const handleSelectAll = (type: "pop" | "flyer", event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked
    if (checked) {
      const newSelections = [...selections]
      props.stores.forEach((store) => {
        const existing = newSelections.find((s) => s.storeId === store.id)
        if (existing) {
          existing[type] = true
        } else {
          newSelections.push({
            storeId: store.id,
            pop: type === "pop" ? true : false,
            flyer: type === "flyer" ? true : false,
          })
        }
      })
      setSelections(newSelections)
    } else {
      const newSelections = selections.map((s) => ({ ...s, [type]: false }))
      setSelections(newSelections)
    }
  }

  const handleCreatePdf = async () => {
    const validSelections = selections.filter((s) => s.pop || s.flyer)
    await props.onCreatePdf(validSelections)
  }

  const getSelection = (storeId: number) => {
    return selections.find((s) => s.storeId === storeId) || { storeId, pop: false, flyer: false }
  }

  const getTotalSelectedCount = () => {
    return selections.filter((s) => s.pop || s.flyer).length
  }

  const getAllSelectedCount = (type: "pop" | "flyer") => {
    return selections.filter((s) => s[type]).length
  }

  return (
    <Wrapper className={props.className}>
      <div className={`${props.className}-header`}>
        <div className={`${props.className}-title-section`}>
          <h2>加盟店一覧</h2>
          <p className={`${props.className}-notice`}>
            ※ labelが設定されていない加盟店は表示されません
          </p>
        </div>
        <div className={`${props.className}-header-buttons`}>
          <Button
            onClick={props.onRefresh}
            disabled={props.isRefreshing}
            className={`${props.className}-refresh-button`}
          >
            {props.isRefreshing ? "読み込み中..." : "再読み込み"}
          </Button>
          <Button
            onClick={handleCreatePdf}
            disabled={getTotalSelectedCount() === 0 || props.isCreating}
          >
            {props.isCreating ? "作成中..." : `PDFを作成 (${getTotalSelectedCount()})`}
          </Button>
        </div>
      </div>

      <div className={`${props.className}-controls`}>
        <div className={`${props.className}-select-all`}>
          <label className={`${props.className}-simple-checkbox`}>
            <input
              type="checkbox"
              checked={
                getAllSelectedCount("pop") === props.stores.length && props.stores.length > 0
              }
              onChange={(event) => handleSelectAll("pop", event)}
            />
            <span>ポップ全て選択 ({getAllSelectedCount("pop")})</span>
          </label>
          <label className={`${props.className}-simple-checkbox`}>
            <input
              type="checkbox"
              checked={
                getAllSelectedCount("flyer") === props.stores.length && props.stores.length > 0
              }
              onChange={(event) => handleSelectAll("flyer", event)}
            />
            <span>ビラ全て選択 ({getAllSelectedCount("flyer")})</span>
          </label>
        </div>
      </div>

      <ul className={`${props.className}-stores`}>
        {props.stores.map((store) => {
          const selection = getSelection(store.id)
          return (
            <li key={store.id} className={`${props.className}-stores__item`}>
              <div className={`${props.className}-store-content`}>
                <div className={`${props.className}-store-info`}>
                  <div className={`${props.className}-store-item`}>
                    <span className={`${props.className}-store-item__title`}>ID</span>
                    <span className={`${props.className}-store-item__content`}>{store.id}</span>
                  </div>
                  <div className={`${props.className}-store-item`}>
                    <span className={`${props.className}-store-item__title`}>ストア名</span>
                    <span className={`${props.className}-store-item__content`}>{store.name}</span>
                  </div>
                </div>
                <div className={`${props.className}-checkboxes`}>
                  <label className={`${props.className}-simple-checkbox`}>
                    <input
                      type="checkbox"
                      checked={selection.pop}
                      onChange={(event) => handleStoreSelect(store.id, "pop", event)}
                    />
                    <span>ポップ</span>
                  </label>
                  <label className={`${props.className}-simple-checkbox`}>
                    <input
                      type="checkbox"
                      checked={selection.flyer}
                      onChange={(event) => handleStoreSelect(store.id, "flyer", event)}
                    />
                    <span>ビラ</span>
                  </label>
                </div>
              </div>
            </li>
          )
        })}
      </ul>

      <div className={`${props.className}-footer`}>
        {props.hasMore && <Button onClick={props.onLoadMore}>もっと読む</Button>}
        <Button
          onClick={handleCreatePdf}
          disabled={getTotalSelectedCount() === 0 || props.isCreating}
        >
          {props.isCreating ? "作成中..." : `PDFを作成 (${getTotalSelectedCount()})`}
        </Button>
      </div>
    </Wrapper>
  )
}

const StyledComponent = styled(Component)`
  padding: 60px 0;

  &-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 20px;
  }

  &-header-buttons {
    display: flex;
    gap: 12px;
    margin-top: 8px;

    button {
      padding: 12px 24px;
      font-size: 16px;
      font-weight: bold;
      min-height: 48px;
    }
  }

  &-refresh-button {
    background-color: ${color.lightGray} !important;
    color: ${color.darkGray} !important;
    
    &:hover:not(:disabled) {
      background-color: ${color.darkGray} !important;
      color: white !important;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  &-title-section {
    h2 {
      ${font(24)};
      margin: 0 0 4px 0;
    }
  }

  &-notice {
    ${font(12)};
    color: ${color.darkGray};
    margin: 0;
    font-style: italic;
  }

  &-controls {
    margin-bottom: 20px;
    padding: 10px 0;
    border-bottom: 1px solid ${color.lightGray};
  }

  &-select-all {
    display: flex;
    gap: 20px;
  }

  &-simple-checkbox {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 4px;
    transition: background-color 0.2s ease;
    
    &:hover {
      background-color: ${color.lightGray};
    }
    
    input[type="checkbox"] {
      margin: 0;
      width: 18px;
      height: 18px;
      border: 2px solid ${color.darkGray};
      border-radius: 3px;
      appearance: none;
      cursor: pointer;
      position: relative;
      background-color: white;
      
      &:checked {
        background-color: ${color.main};
        border-color: ${color.main};
        
        &::after {
          content: '✓';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          color: white;
          font-size: 12px;
          font-weight: bold;
        }
      }
      
      &:focus {
        outline: 2px solid ${color.main};
        outline-offset: 2px;
      }
    }
    
    span {
      ${font(14)};
      user-select: none;
      font-weight: 500;
    }
  }

  &-stores {
    &__item {
      margin-bottom: 12px;
      border-radius: 8px;
      width: 100%;
      ${boxShadow.deep};
      padding: 16px 18px;
    }
  }

  &-store-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &-store-info {
    display: flex;
    gap: 40px;
    flex: 1;
  }

  &-checkboxes {
    display: flex;
    gap: 16px;
    margin-left: 20px;
  }

  &-store-item {
    display: flex;
    flex-direction: column;

    &__title {
      ${font(13)};
      color: ${color.darkGray};
      margin-bottom: 4px;
    }

    &__content {
      ${font(15)};
      font-weight: bold;
    }
  }

  &-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 20px;
    padding-top: 20px;
    border-top: 1px solid ${color.lightGray};

    button {
      padding: 12px 24px;
      font-size: 16px;
      font-weight: bold;
      min-height: 48px;
    }
  }
`

const Container: React.FC<ContainerProps> = (props) => {
  return <StyledComponent {...props} />
}

export const StoresList = Container
